(ns minorodata.analytics-models.api
  (:require [minorodata.analytics-models.model :refer [->analytics-model-registry ->analytics-model]]
            [minorodata.app.posthog-events :as posthog-events]
            [minorodata.sandbox.api :refer [api-url]]
            [mold-http.core :refer [reg-event-http]]))


;; Backend contract (kioku FT-2497, PR #1806):
;;   GET    /api/analytics-models            list  -> {:results [..] :pagination .. :sort ..}
;;   POST   /api/analytics-models            create -> {:result :created :data <row>}
;;   GET    /api/analytics-models/:model-id  read  -> <row>
;;   PUT    /api/analytics-models/:model-id  update -> {:result :updated :data <row>}
;;   DELETE /api/analytics-models/:model-id  delete -> {:result :deleted}
;;   POST   /api/analytics-models/:model-id/up-version
;;                                           -> {:result :up-versioned :data <row>}  (FT-2569)
;;
;; Request body for create/update is the backend `model-body` (kebab-case):
;;   name, status ("active"|"paused"), registry-model, version,
;;   input-tables, output-tables, description?, run-mode?, model-params?, ...


(defn- row->analytics-model
  "Map a backend `analytics.model` row straight to the FE entity — field names
   match the wire contract, so the whole row passes through unchanged."
  [row]
  (->analytics-model row))


(defn- ->model-body
  "Build the backend `model-body` from a FE create/update payload."
  [{:keys [name status project-name version params input-tables output-tables
           description run-mode cron]}]
  (cond-> {:name           name
           :status         (or status "active")
           :registry-model project-name
           :version        version
           :input-tables   (or input-tables [])
           :output-tables  (or output-tables [])
           :model-params   (or params {})}
    description (assoc :description description)
    run-mode    (assoc :run-mode run-mode)
    cron        (assoc :schedule-spec {:cron cron})))


;; =============================================================================
;; Fetch registry-model catalogue
;;   GET /api/analytics-models/registry
;;     -> {:models ["model-a" "model-b" ...]}   (just the names)
;;   GET /api/analytics-models/registry/:model/latest-version
;;     -> full config for one model
;;
;; The list endpoint only returns model names, so on success we fan out one
;; latest-version request per model to hydrate the registry catalogue.
;; =============================================================================

(defn- normalize-option
  "Select options arrive with `:label`, but the generator reads an option's text
   from `:name` (see `convert-select-option`)."
  [option]
  (if (and (map? option) (contains? option :label))
    (-> option (assoc :name (:label option)) (dissoc :label))
    option))


(defn- normalize-ui-block
  "`custom-ui-blocks` come from JSON, so `:block-type`/`:name` arrive as strings.
   The form generator keys off keyword block-types, so coerce them back; select
   options also need their `:label` renamed to `:name`."
  [block]
  (cond-> block
    (:block-type block) (update :block-type keyword)
    (:name block)       (update :name keyword)
    (:options block)    (update :options #(mapv normalize-option %))))


(defn- registry-version->model
  "Map a backend latest-version payload to the FE `analytics-model-registry`
   entity. The rich config (display name, description, table definitions,
   custom UI blocks, ...) lives in the build manifest's `app-definition`;
   project-name/version come from the registry pointer."
  [results]
  (let [app-definition (:app-definition results)]
    (->analytics-model-registry
     (merge app-definition
            {:project-name     (:model results)
             :version          (:version results)
             :task-definition  (:task-definition results)
             :commit-sha       (:commit-sha results)
             :custom-ui-blocks (mapv normalize-ui-block (:custom-ui-blocks app-definition))}))))


(defn fetch-model-registry-request [_ _]
  {:http-xhrio {:id  :analytics-models.api/fetch-model-registry
                :url "/api/analytics-models/registry"}})


(defn fetch-model-registry-callback [_ [error results]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to fetch analytics models"]}
    {:dispatch-n (mapv (fn [model]
                         [:analytics-models.api/fetch-model-registry-version model])
                       (:models results))}))


(reg-event-http
 :analytics-models.api/fetch-model-registry
 fetch-model-registry-request
 fetch-model-registry-callback)


;; -----------------------------------------------------------------------------
;; Fetch single registry model's latest version
;; (GET /api/analytics-models/registry/:model/latest-version)
;; -----------------------------------------------------------------------------

(defn fetch-model-registry-version-request [_ [model]]
  {:http-xhrio {:id        :analytics-models.api/fetch-model-registry-version
                :url       (api-url "/api/analytics-models/registry/{model}/latest-version"
                                    {:model model})
                :cb-params {:model model}}})


(defn fetch-model-registry-version-callback [_ [error results {:keys [model]}]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error (str "Failed to fetch analytics model \"" model "\"")]}
    {:dispatch [:analytics-model-registry/add-many [(registry-version->model results)]]}))


(reg-event-http
 :analytics-models.api/fetch-model-registry-version
 fetch-model-registry-version-request
 fetch-model-registry-version-callback)


;; =============================================================================
;; Fetch models (GET /api/analytics-models)
;; =============================================================================

(defn fetch-models-request [_ _]
  {:http-xhrio {:id  :analytics-models.api/fetch-models
                :url "/api/analytics-models"}})


(defn fetch-models-callback [_ [error results]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to fetch analytics models"]}
    {:dispatch-n [[:analytics-model/add-many (mapv row->analytics-model (:results results))]
                  ;; both graphs group model nodes by their instance's registry
                  ;; model, so re-lay-out once the instances are loaded.
                  [:dependency-graph.events/layout-all]
                  [:sandbox.events/relayout-all]]}))


(reg-event-http
 :analytics-models.api/fetch-models
 fetch-models-request
 fetch-models-callback)


;; =============================================================================
;; Fetch model last-run statuses (GET /api/analytics-models/statuses)
;;   -> {:statuses [{:model-id :status :created-at :started-at
;;                   :finished-at :is-executing} ...]}
;; Stored raw in the shared status store under `:model` (see
;; `minorodata.statuses.events`); the sidebar/detail join a status onto an instance
;; by :model-id.
;; =============================================================================

(defn fetch-statuses-request [_ _]
  {:http-xhrio {:id  :analytics-models.api/fetch-statuses
                :url "/api/analytics-models/statuses"}})


(defn fetch-statuses-callback [_ [error results]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to fetch analytics model statuses"]}
    {:dispatch [:statuses.events/upsert :model (:statuses results)]}))


(reg-event-http
 :analytics-models.api/fetch-statuses
 fetch-statuses-request
 fetch-statuses-callback)


;; =============================================================================
;; Fetch single model (GET /api/analytics-models/:model-id)
;; =============================================================================

(defn fetch-model-request [_ [{:keys [id]}]]
  {:http-xhrio {:id        :analytics-models.api/fetch-model
                :url       (api-url "/api/analytics-models/{model-id}"
                                    {:model-id id})
                :cb-params {:id id}}})


(defn fetch-model-callback [_ [error results {:keys [id]}]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to fetch analytics model"]}
    {:dispatch [:analytics-model/update
                [:analytics-model/model-id id]
                (row->analytics-model results)
                {:replace true}]}))


(reg-event-http
 :analytics-models.api/fetch-model
 fetch-model-request
 fetch-model-callback)


;; =============================================================================
;; Create model (POST /api/analytics-models)
;; =============================================================================

(defn create-model-request [_ [payload]]
  {:http-xhrio {:id        :analytics-models.api/create-model
                :method    :post
                :url       "/api/analytics-models"
                :params    (->model-body payload)
                :cb-params payload}})


(defn create-model-callback [_ [error results {:keys [on-success]}]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to create analytics model"]}
    (let [model (row->analytics-model (:data results))]
      (when on-success (on-success model))
      {:dispatch-n [[:analytics-models.api/fetch-models]
                    [:analytics-models.api/fetch-statuses]
                    [:app.events/add-snack :success "Successfully created an analytics model"]]})))


(reg-event-http
 :analytics-models.api/create-model
 create-model-request
 create-model-callback)


;; =============================================================================
;; Update model (PUT /api/analytics-models/:model-id)
;; =============================================================================

(defn update-model-request [_ [{:keys [id] :as payload}]]
  {:http-xhrio {:id        :analytics-models.api/update-model
                :method    :put
                :url       (api-url "/api/analytics-models/{model-id}"
                                    {:model-id id})
                :params    (->model-body payload)
                :cb-params payload}})


(defn update-model-callback [_ [error results {:keys [id on-success]}]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to update analytics model"]}
    (let [model (row->analytics-model (:data results))]
      (when on-success (on-success model))
      {:dispatch-n [[:analytics-model/delete [:analytics-model/model-id id]]
                    [:analytics-models.api/fetch-models]
                    [:analytics-models.api/fetch-statuses]
                    [:app.events/add-snack :success "Successfully updated the analytics model"]]})))


(reg-event-http
 :analytics-models.api/update-model
 update-model-request
 update-model-callback)


;; =============================================================================
;; Delete model (DELETE /api/analytics-models/:model-id)
;; =============================================================================

(defn delete-model-request [_ [{:keys [id] :as payload}]]
  {:http-xhrio {:id        :analytics-models.api/delete-model
                :method    :delete
                :url       (api-url "/api/analytics-models/{model-id}"
                                    {:model-id id})
                :cb-params payload}})


(defn delete-model-callback [_ [error _results {:keys [id on-success]}]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to delete analytics model"]}
    (do
      (when on-success (on-success))
      {:dispatch-n [[:analytics-model/delete [:analytics-model/model-id id]]
                    [:analytics-models.api/fetch-models]
                    [:app.events/add-snack :success "Successfully deleted the analytics model"]]})))


(reg-event-http
 :analytics-models.api/delete-model
 delete-model-request
 delete-model-callback)


;; =============================================================================
;; Up-version model (POST /api/analytics-models/:model-id/up-version)
;;   Re-pins the instance to the latest task-def revision of its current
;;   version. Like update, the server versions the row (new model-id, same
;;   master-id) and returns {:result :up-versioned :data <row>}, so on success
;;   we drop the old entity, refetch, and let the caller navigate to the new id.
;; =============================================================================

(defn up-version-model-request [_ [{:keys [id] :as payload}]]
  {:http-xhrio {:id        :analytics-models.api/up-version-model
                :method    :post
                :url       (api-url "/api/analytics-models/{model-id}/up-version"
                                    {:model-id id})
                :cb-params payload}})


(defn up-version-model-callback [_ [error results {:keys [id on-success]}]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to up-version analytics model"]}
    (let [model (row->analytics-model (:data results))]
      (when on-success (on-success model))
      {:dispatch-n [[:analytics-model/delete [:analytics-model/model-id id]]
                    [:analytics-models.api/fetch-models]
                    [:analytics-models.api/fetch-statuses]
                    [:app.events/add-snack :success "Successfully up-versioned the analytics model"]]})))


(reg-event-http
 :analytics-models.api/up-version-model
 up-version-model-request
 up-version-model-callback)


;; =============================================================================
;; Run model (POST /api/toaster/job/one-time, task/type "data-model")
;;   -> {:results {:job/id .. :job-execution/id ..}}
;; =============================================================================

(def POLL_INTERVAL_MS 2500)

;; A job outlives the model's own task, but not indefinitely. Without a cap any
;; status outside #{"success" "failed"} polls for the rest of the session.
(def MAX_POLLS 480)


(defn run-model-request [_ [{:keys [id]}]]
  {:http-xhrio      {:id        :analytics-models.api/run-model
                     :method    :post
                     :url       "/api/toaster/job/one-time"
                     :params    {"job/tasks" [{"task/type"   "data-model"
                                               "task/params" {"model-id" id}}]}
                     :cb-params {:id id}}
   :posthog/capture {:event (:data-model/run-triggered posthog-events/events)
                     :props {:model-id id}}})


(defn run-model-callback [_ [error results _]]
  (if (some? error)
    {:dispatch-n [[:app.events/add-snack :error "Failed to start the analytics model"]
                  [:app.api/log :error "Failed to start the analytics model" error]]}
    {:dispatch-n [[:app.events/add-snack :success "Started the analytics model"]
                  [:analytics-models.api/poll-run (get-in results [:results :job-execution/id]) 1]]}))


(reg-event-http
 :analytics-models.api/run-model
 run-model-request
 run-model-callback)


;; =============================================================================
;; Poll a model run (GET /api/toaster/job-execution/:job-id)
;;   -> {:results {:job/status "running"|"success"|"failed"
;;                 :job/start-time ..
;;                 :job/task-executions [{:model/running [..] :model/failed [..] ..}]}}
;; The job also queues everything downstream of the model, so it outlives the
;; model's own task.
;; =============================================================================

(def bucket->status
  {:model/starting  {:status "pending"}
   :model/running   {:status "pending" :is-executing true}
   :model/completed {:status "completed"}
   :model/failed    {:status "failed"}
   :model/skipped   {:status "skipped"}})


(defn task-execution->statuses
  "Bucket lists -> status-store rows, dated with the job's start time so the run
   in flight beats the previous run in `statuses.domain/newer-run`."
  [task-execution created-at]
  (for [[bucket status] bucket->status
        model-id        (get task-execution bucket)]
    (assoc status :model-id model-id :created-at created-at)))


(defn poll-run-request [_ [job-id attempt]]
  {:http-xhrio {:url       (api-url "/api/toaster/job-execution/{job-id}" {:job-id job-id})
                :cb-params {:job-id job-id :attempt attempt}}})


(defn- run-finished-snack [status task-executions]
  (if (or (= "failed" status) (some (comp seq :model/failed) task-executions))
    [:app.events/add-snack :error "Analytics model run failed"]
    [:app.events/add-snack :success "Analytics model run finished"]))


(defn poll-run-callback [_ [error results {:keys [job-id attempt]}]]
  (if (some? error)
    ;; stop on error, but resync first - the last write was mid-run, so without
    ;; this the pill stays on "Running..." until the page reloads
    {:dispatch-n [[:analytics-models.api/fetch-statuses]
                  [:app.events/add-snack :error "Failed to fetch the model run status"]
                  [:app.api/log :error "Failed to fetch the model run status" error]]}
    (let [{:job/keys [status start-time task-executions]} (:results results)
          finished? (contains? #{"success" "failed"} status)
          give-up?  (>= attempt MAX_POLLS)
          statuses  (mapcat #(task-execution->statuses % start-time) task-executions)]
      (cond
        finished?
        {:dispatch-n [[:statuses.events/upsert :model statuses]
                      [:analytics-models.api/fetch-statuses]
                      (run-finished-snack status task-executions)]}

        give-up?
        {:dispatch-n [[:statuses.events/upsert :model statuses]
                      [:analytics-models.api/fetch-statuses]
                      [:app.events/add-snack :warning
                       "The model run is still going. Refresh to see the result."]]}

        :else
        {:dispatch-n     [[:statuses.events/upsert :model statuses]]
         :dispatch-later [{:ms       POLL_INTERVAL_MS
                           :dispatch [:analytics-models.api/poll-run job-id (inc attempt)]}]}))))


(reg-event-http
 :analytics-models.api/poll-run
 poll-run-request
 poll-run-callback)
